import {
  StationeryItem,
  StationeryOptions,
  getStationeryById
} from "@coxauto-ui/cxm/communications/api";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import styled from "styled-components";
import { ConversationContext, useEmailReply } from "../../../context";

const StyledCustomStationery = styled.div`
  position: relative;

  /* Target dropdown button text - scoped to avoid modal conflicts */
  .k-dropdown .k-dropdown-wrap .k-input,
  .k-dropdown .k-input,
  .k-picker .k-picker-wrap .k-input,
  .k-picker .k-input {
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    max-width: 145px !important;
    display: inline-block !important;
  }

  /* Target selected value text display - all Kendo input variations */
  span.k-input,
  .k-dropdown-wrap span,
  .k-picker-wrap span,
  .k-dropdown-wrap .k-dropdown-wrap-inner,
  .k-picker-wrap .k-picker-wrap-inner {
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    display: inline-block !important;
    max-width: 145px !important;
    width: 145px !important;
  }

  /* Ensure the dropdown container doesn't wrap */
  .k-dropdown,
  .k-picker {
    white-space: nowrap !important;
    overflow: hidden !important;
  }
`;

export interface CustomStationeryProps {
  /** Optional CSS class name for custom styling */
  className?: string;
  /** Optional inline styles */
  style?: React.CSSProperties;
  /** Stationery options including array of StationeryItems and default ID */
  stationeryOptions: StationeryOptions;
}

/**
 * Custom Stationery dropdown component for the WYSIWYG editor toolbar
 * Provides pre-designed HTML templates that can be inserted into the editor
 * Matches the width of the CustomFormatBlock component (180px)
 */
export const CustomStationery = memo((props: CustomStationeryProps) => {
  const { setSelectedStationeryHTML } = useEmailReply();
  const { env, vinDealerId } = useContext(ConversationContext);

  const initialStationery = useMemo((): StationeryItem => {
    // If no stationery options provided, return empty
    if (props.stationeryOptions.stationeries.length === 0) {
      return {} as StationeryItem;
    }

    // Return default if available
    if (props.stationeryOptions.defaultDealerEmailStationeryID) {
      const defaultStationery = props.stationeryOptions.stationeries.find(
        item =>
          item.stationeryId ===
          props.stationeryOptions.defaultDealerEmailStationeryID
      );
      if (defaultStationery) {
        return defaultStationery;
      }
    }
    // otherwise, return the first.
    return props.stationeryOptions.stationeries[0];
  }, [props.stationeryOptions]);

  const [selectedStationery, setSelectedStationery] =
    useState(initialStationery);

  useEffect(() => {
    const loadStationeryHTML = async () => {
      try {
        const getStationeryByIdRequest = {
          dealerId: vinDealerId,
          stationeryId: selectedStationery.stationeryId
        };
        const response = await getStationeryById(getStationeryByIdRequest, env);
        setSelectedStationeryHTML(response);
      } catch (error) {
        setSelectedStationeryHTML("");
      }
    };
    if (selectedStationery && selectedStationery.stationeryId !== 0) {
      loadStationeryHTML();
    } else {
      // If no stationery is selected (stationeryId = 0), set HTML to empty string
      setSelectedStationeryHTML("");
    }
  }, [vinDealerId, env, selectedStationery, setSelectedStationeryHTML]);

  const handleStationeryChange = useCallback(
    (event: { target: { value: StationeryItem } }) => {
      const selected = event.target.value;
      setSelectedStationery(selected);
    },
    []
  );

  const handleDropdownOpen = useCallback(() => {
    setTimeout(() => {
      const popup = document.querySelector(
        ".k-animation-container:not([data-stationery-dropdown])"
      );
      if (popup && popup.querySelector(".k-list-item")) {
        popup.setAttribute("data-stationery-dropdown", "true");
      }
    }, 10);
  }, []);

  // Add tooltips to dropdown items when they're rendered
  useEffect(() => {
    const addTooltipsToDropdownItems = () => {
      const listItems = document.querySelectorAll(".k-list-item");
      listItems.forEach((item, index) => {
        if (
          props.stationeryOptions.stationeries[index] &&
          item instanceof HTMLElement
        ) {
          item.setAttribute(
            "title",
            props.stationeryOptions.stationeries[index].stationeryName
          );
        }
      });
    };

    // Add tooltips when dropdown opens
    const observer = new MutationObserver(() => {
      setTimeout(addTooltipsToDropdownItems, 50);
    });

    // Monitor for ViewHtml dialog opening and close our dropdown if it's open
    const handleDialogOpen = () => {
      const stationeryPopup = document.querySelector(
        ".k-animation-container[data-stationery-dropdown]"
      );
      if (stationeryPopup) {
        // Close the stationery dropdown if ViewHtml modal opens
        const closeButton = stationeryPopup.parentElement?.querySelector(
          ".k-dropdown .k-select"
        );
        if (closeButton instanceof HTMLElement) {
          closeButton.click();
        }
      }
    };

    // Observe changes to the document body to catch when dropdown or dialog opens
    if (typeof document !== "undefined") {
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      // Listen for dialog/modal openings
      const dialogObserver = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          mutation.addedNodes.forEach(node => {
            if (
              node instanceof HTMLElement &&
              (node.classList.contains("k-dialog-wrapper") ||
                node.classList.contains("k-window-wrapper") ||
                node.querySelector?.(".k-dialog, .k-window"))
            ) {
              handleDialogOpen();
            }
          });
        });
      });

      dialogObserver.observe(document.body, {
        childList: true,
        subtree: true
      });

      return () => {
        observer.disconnect();
        dialogObserver.disconnect();
      };
    }

    return () => observer.disconnect();
  }, [props.stationeryOptions]);

  return (
    <StyledCustomStationery>
      <div
        style={{ display: "inline-block", width: "100%" }}
        title={selectedStationery?.stationeryName || ""}
      >
        <DropDownList
          className={props.className}
          data={props.stationeryOptions.stationeries}
          dataItemKey="stationeryId"
          popupSettings={{ width: "180px" }}
          style={{
            ...props.style,
            width: "135px"
          }}
          textField="stationeryName"
          title="Stationery"
          value={selectedStationery}
          onChange={handleStationeryChange}
          onOpen={handleDropdownOpen}
        />
      </div>
    </StyledCustomStationery>
  );
});

// Add display name for debugging purposes
CustomStationery.displayName = "CustomStationery";

export default CustomStationery;
